import axios from 'axios';
import { ApiError } from '../types/api';
import { queryClient } from './queryClient';

const getApiBaseUrl = () => {
  // Always use production backend (onrender)
  return import.meta.env.VITE_ENVPROD || 'https://activitynavigator-backend.onrender.com';
};

const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to clear auth state
const clearAuthState = () => {
  // Clear localStorage
  localStorage.removeItem('authToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userId');
  
  // Clear query cache to trigger reactive updates in all components
  queryClient.setQueryData(['auth'], null);
  
  // Invalidate auth query to ensure fresh state on next access
  queryClient.invalidateQueries({ queryKey: ['auth'] });
};

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || error.response?.data?.error || '';
    
    // Handle authentication errors (400 for invalid token, 401 for unauthorized/expired)
    if (status === 401 || (status === 400 && (
      errorMessage.toLowerCase().includes('token') ||
      errorMessage.toLowerCase().includes('invalid') ||
      errorMessage.toLowerCase().includes('authenticate') ||
      errorMessage.toLowerCase().includes('log in')
    ))) {
      // Token expired or invalid - logout user per CONTEXT.md requirements
      clearAuthState();
      
      // Redirect to home page
      window.location.href = '/';
      
      // Show session expired message (will be handled by app-level notification)
      // Note: Snackbar/toast implementation deferred to Phase 3
    }
    return Promise.reject(error);
  }
);

export default apiClient;
